import React, { useEffect, useMemo, useState } from "react";
import {
  Check,
  Loader2,
  Palette,
  Sparkles,
  Tags,
  X,
} from "lucide-react";

import TagChip from "src/components/TagChip";
import {
  TAG_COLORS,
  TAG_ICONS,
  TAG_ICON_NAMES,
} from "src/components/tagIcons";

const EMPTY = {
  name: "",
  color: TAG_COLORS[0],
  icon: "tag",
  description: "",
};

function TagFormModal({ open, tag, onClose, onSubmit }) {
  const isEdit = Boolean(tag?.id);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    setForm(tag
      ? {
          name: tag.name || "",
          color: tag.color || TAG_COLORS[0],
          icon: tag.icon || "tag",
          description: tag.description || "",
        }
      : EMPTY);
    setError("");
  }, [open, tag]);

  const preview = useMemo(
    () => ({
      name: form.name.trim() || "Tag name",
      color: form.color,
      icon: form.icon,
    }),
    [form],
  );

  if (!open) return null;

  const submit = async () => {
    if (!form.name.trim()) {
      setError("Give the tag a name.");
      return;
    }

    try {
      setSaving(true);
      setError("");
      await onSubmit({
        name: form.name.trim(),
        color: form.color,
        icon: form.icon || null,
        description: form.description.trim() || null,
      });
      onClose();
    } catch (err) {
      const data = err?.response?.data;
      setError(
        data?.message
          || Object.values(data?.errors || {})[0]?.[0]
          || "Could not save the tag.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-3 sm:p-5">
      <div
        className="absolute inset-0 bg-[#172a33]/25 backdrop-blur-[5px]"
        onClick={onClose}
      />

      <div className="relative z-10 flex max-h-[92vh] w-full max-w-[620px] flex-col overflow-hidden rounded-[26px] border border-[#d9e4df] bg-[#ffffff] shadow-[0_30px_90px_rgba(27,52,42,0.22)] dark:border-[#354d44] dark:bg-[#131f1b]">
        <div className="flex items-start justify-between gap-4 border-b border-[#eef3f6] px-5 py-5 sm:px-6 dark:border-[#2d423b]">
          <div className="flex min-w-0 items-center gap-3.5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[13px] border border-[#c7dce5] bg-[#eaf4fa] text-[#147db5] dark:border-[#3a5b4e] dark:bg-[#1c342b] dark:text-[#78c7a8]">
              <Tags size={17} strokeWidth={1.8} />
            </div>
            <div className="min-w-0">
              <h2 className="text-[16px] font-bold tracking-[-0.025em] text-[#21372f] dark:text-[#e2ece7]">
                {isEdit ? "Edit tag" : "New tag"}
              </h2>
              <p className="mt-1 text-[10px] font-medium text-[#819089] dark:text-[#7c9188]">
                Create a clear label your whole team can recognise.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close tag editor"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#81918a] transition-colors hover:bg-[#eef2ef] hover:text-[#344b42] dark:hover:bg-[#23362f] dark:hover:text-[#c5d7cf]"
          >
            <X size={16} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
          {error ? (
            <p className="mb-4 rounded-xl border border-[#edcfcd] bg-[#fff5f4] px-3.5 py-2.5 text-[10px] font-semibold text-[#a15250] dark:border-[#643f3d] dark:bg-[#2f2120] dark:text-[#e5a09d]">
              {error}
            </p>
          ) : null}

          <div className="relative overflow-hidden rounded-[18px] border border-[#e1eaef] bg-[linear-gradient(120deg,#faf8f2_0%,#f0f7f3_54%,#edf6f9_100%)] px-4 py-4 dark:border-[#324a41] dark:bg-[linear-gradient(120deg,#1d2822_0%,#1b2d26_54%,#192a30_100%)]">
            <div className="pointer-events-none absolute -right-7 -top-9 h-28 w-28 rounded-full border border-dashed border-[#bdd8cd] dark:border-[#3e5d51]" />
            <div className="relative flex items-center justify-between gap-4">
              <div>
                <p className="flex items-center gap-1.5 text-[8px] font-bold uppercase tracking-[0.14em] text-[#7a8b84] dark:text-[#7f948b]">
                  <Sparkles size={10} className="text-[#b77732]" />
                  Live preview
                </p>
                <p className="mt-2 text-[10px] font-medium text-[#71827b] dark:text-[#7d9188]">
                  This is how the tag appears across Auto Appy.
                </p>
              </div>
              <div className="relative shrink-0">
                <div
                  className="absolute inset-0 rounded-full blur-xl"
                  style={{ backgroundColor: `${form.color}20` }}
                />
                <div className="relative">
                  <TagChip tag={preview} />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-[10px] font-bold text-[#34506a] dark:text-[#a8bdb4]">
                Tag name
              </span>
              <input
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                maxLength={60}
                placeholder="e.g. VIP customer"
                className="h-11 w-full rounded-xl border border-[#e1eaef] bg-[#f6f9fb] px-3.5 text-[12px] font-semibold text-[#102f45] outline-none transition-all placeholder:font-medium placeholder:text-[#8a9ba8] hover:border-[#c7dce5] focus:border-[#147db5] focus:bg-white focus:ring-4 focus:ring-[#147db5]/[0.07] dark:border-[#354d44] dark:bg-[#101a16] dark:text-[#e0ebe5]"
              />
            </label>

            <label className="block">
              <span className="mb-1.5 block text-[10px] font-bold text-[#34506a] dark:text-[#a8bdb4]">
                Description <span className="font-medium text-[#8c9a95]">optional</span>
              </span>
              <input
                value={form.description}
                onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
                maxLength={255}
                placeholder="What this tag means"
                className="h-11 w-full rounded-xl border border-[#e1eaef] bg-[#f6f9fb] px-3.5 text-[12px] font-semibold text-[#102f45] outline-none transition-all placeholder:font-medium placeholder:text-[#8a9ba8] hover:border-[#c7dce5] focus:border-[#147db5] focus:bg-white focus:ring-4 focus:ring-[#147db5]/[0.07] dark:border-[#354d44] dark:bg-[#101a16] dark:text-[#e0ebe5]"
              />
            </label>
          </div>

          <div className="mt-5 rounded-[18px] border border-[#e1eaef] bg-[#ffffff] p-4 dark:border-[#30473f] dark:bg-[#16221e]">
            <div className="flex items-center gap-2">
              <Palette size={14} className="text-[#b77732]" />
              <div>
                <h3 className="text-[10px] font-bold text-[#34506a] dark:text-[#b8cbc2]">Colour</h3>
                <p className="mt-0.5 text-[9px] font-medium text-[#8a9ba8] dark:text-[#758a81]">Choose a tone that carries meaning at a glance.</p>
              </div>
            </div>

            <div className="mt-3.5 flex flex-wrap gap-2">
              {TAG_COLORS.map((color) => {
                const active = form.color === color;

                return (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setForm((current) => ({ ...current, color }))}
                    aria-label={`Use colour ${color}`}
                    className={`flex h-7 w-7 items-center justify-center rounded-[9px] border transition-all hover:scale-110 ${active ? "scale-110 shadow-[0_5px_12px_rgba(34,55,46,0.14)]" : "border-transparent"}`}
                    style={{
                      backgroundColor: color,
                      borderColor: active ? "rgba(255,255,255,0.95)" : "transparent",
                      boxShadow: active ? `0 0 0 2px ${color}55` : undefined,
                    }}
                  >
                    {active ? <Check size={12} className="text-white" strokeWidth={3} /> : null}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-4 rounded-[18px] border border-[#e1eaef] bg-[#ffffff] p-4 dark:border-[#30473f] dark:bg-[#16221e]">
            <div className="flex items-center gap-2">
              <Tags size={14} className="text-[#147db5]" />
              <div>
                <h3 className="text-[10px] font-bold text-[#34506a] dark:text-[#b8cbc2]">Icon</h3>
                <p className="mt-0.5 text-[9px] font-medium text-[#8a9ba8] dark:text-[#758a81]">Pick the fastest visual cue for your team.</p>
              </div>
            </div>

            <div className="mt-3.5 grid grid-cols-7 gap-2 sm:grid-cols-10">
              {TAG_ICON_NAMES.map((name) => {
                const Icon = TAG_ICONS[name];
                const active = form.icon === name;

                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setForm((current) => ({ ...current, icon: name }))}
                    aria-label={`Use ${name} icon`}
                    className={`flex h-8 w-8 items-center justify-center rounded-[10px] border transition-all ${active ? "border-[#b9dff0] bg-[#eaf4fa] text-[#147db5] shadow-[0_4px_12px_rgba(36,135,101,0.10)] dark:border-[#47705f] dark:bg-[#1e3a2f] dark:text-[#78c7a8]" : "border-[#e0e6e2] bg-white text-[#7c8c85] hover:border-[#bdd0c7] hover:text-[#3c5a4d] dark:border-[#374d45] dark:bg-[#182620] dark:text-[#82988e]"}`}
                  >
                    <Icon size={14} strokeWidth={1.8} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2.5 border-t border-[#eef3f6] bg-[#f6f9fb] px-5 py-4 sm:px-6 dark:border-[#2d423b] dark:bg-[#17231f]">
          <button
            type="button"
            onClick={onClose}
            className="h-10 rounded-full border border-[#d8e1dc] bg-white px-4 text-[10px] font-bold text-[#63766f] transition-colors hover:bg-[#f0f3f0] dark:border-[#385047] dark:bg-[#1a2924] dark:text-[#a8bdb4]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="inline-flex h-10 items-center gap-2 rounded-full bg-[linear-gradient(135deg,#2d73bd,#2294c2)] px-5 text-[10px] font-bold text-white shadow-[0_8px_22px_rgba(45,115,189,0.20)] transition-all hover:-translate-y-0.5 hover:shadow-[0_11px_26px_rgba(45,115,189,0.26)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} strokeWidth={2.5} />}
            {isEdit ? "Save tag" : "Create tag"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TagFormModal;
